import { useState, useCallback, useEffect, useRef } from 'react'
import { parseIntervalToDays } from '~/common/utils/date-utils'

const WEEK_CONTAINER_WIDTH = 32
const PIXELS_PER_DAY = WEEK_CONTAINER_WIDTH / 7  // ~4.57px

// Minimum durations for each task type (in days)
const MIN_TASK_DURATIONS = {
  seeding: 1,
  transplanting: 1,
  weeding: 3,
  fertilizing: 1,
  harvesting: 7,
  default: 1
}

/**
 * Custom hook for managing task boundary drag interactions
 *
 * Handle types:
 * - { type: 'start', taskIndex: number }: Dragging the start of a specific task (end stays fixed)
 * - { type: 'end', taskIndex: number }: Dragging the end of a specific task (start stays fixed)
 *
 * Unlike phases (which must be continuous), tasks are independent and isolated.
 * Dragging one task does NOT affect other tasks (no cascading).
 *
 * @param {Object} options
 * @param {Array} options.tasks - Array of task objects from schedule data
 * @param {Function} options.onTaskAdjust - Callback with updated tasks array
 * @returns {Object} Drag state and handlers
 */
export const useTaskDrag = ({ tasks, onTaskAdjust }) => {
  const [dragState, setDragState] = useState({
    isDragging: false,
    intersectionIndex: null,
    startX: 0,
    startTasks: null,
  })

  const [hoveredIntersection, setHoveredIntersection] = useState(null)

  // Store callbacks in refs to avoid stale closures
  const tasksRef = useRef(tasks)
  const onTaskAdjustRef = useRef(onTaskAdjust)
  const dragStateRef = useRef(dragState)

  useEffect(() => {
    tasksRef.current = tasks
    onTaskAdjustRef.current = onTaskAdjust
    dragStateRef.current = dragState
  }, [tasks, onTaskAdjust, dragState])

  const handleDragStart = useCallback((intersectionIndex, clientX) => {
    const currentTasks = tasksRef.current
    if (!currentTasks || currentTasks.length === 0) return

    setDragState({
      isDragging: true,
      intersectionIndex,
      startX: clientX,
      startTasks: currentTasks.map(t => ({
        ...t,
        _startDuration: parseIntervalToDays(t.duration)
      })),
    })
  }, [])

  const handleDragMove = useCallback((e) => {
    const state = dragStateRef.current
    if (!state.isDragging || state.startTasks === null) return

    const deltaX = e.clientX - state.startX
    const deltaDays = Math.round(deltaX / PIXELS_PER_DAY)

    if (deltaDays === 0) return

    const { intersectionIndex, startTasks } = state

    let updatedTasks

    // Handle object format: { type: 'start'|'end', taskIndex: number }
    if (typeof intersectionIndex === 'object') {
      const { type, taskIndex } = intersectionIndex

      if (type === 'start') {
        // Dragging the start of a specific task (end stays fixed)
        updatedTasks = handleTaskStartDrag(startTasks, taskIndex, deltaDays)
      } else if (type === 'end') {
        // Dragging the end of a specific task (start stays fixed)
        updatedTasks = handleTaskEndDrag(startTasks, taskIndex, deltaDays)
      }
    }

    if (!updatedTasks) return

    // Remove internal _startDuration property before callback
    const cleanedTasks = updatedTasks.map(({ _startDuration, ...task }) => task)

    if (onTaskAdjustRef.current) {
      onTaskAdjustRef.current(cleanedTasks)
    }
  }, [])

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      intersectionIndex: null,
      startX: 0,
      startTasks: null,
    })
  }, [])

  // Attach/detach global mouse listeners
  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleDragMove)
      document.addEventListener('mouseup', handleDragEnd)

      return () => {
        document.removeEventListener('mousemove', handleDragMove)
        document.removeEventListener('mouseup', handleDragEnd)
      }
    }
  }, [dragState.isDragging, handleDragMove, handleDragEnd])

  return {
    isDragging: dragState.isDragging,
    draggingIndex: dragState.intersectionIndex,
    hoveredIntersection,
    setHoveredIntersection,
    handleDragStart,
  }
}

/**
 * Handle dragging the start of a specific task
 * Adjusts task start date while keeping end date fixed
 * This means: dragging left = earlier start = longer task
 *             dragging right = later start = shorter task
 * Other tasks are unaffected (no cascading)
 */
function handleTaskStartDrag(startTasks, taskIndex, deltaDays) {
  const task = startTasks[taskIndex]
  if (!task) return null

  const minDuration = MIN_TASK_DURATIONS[task.task_code] || MIN_TASK_DURATIONS.default

  // Moving start left (negative delta) = grow task
  // Moving start right (positive delta) = shrink task
  const newDuration = task._startDuration - deltaDays

  // Enforce minimum duration
  if (newDuration < minDuration) {
    return null // Can't shrink below minimum
  }

  // Calculate new start date (end date stays fixed, start moves)
  const originalStart = new Date(task.task_window_start)
  const originalEnd = new Date(originalStart)
  originalEnd.setDate(originalEnd.getDate() + task._startDuration)

  // New start = original end - new duration
  const newStartDate = new Date(originalEnd)
  newStartDate.setDate(newStartDate.getDate() - newDuration)

  return startTasks.map((t, idx) => {
    if (idx === taskIndex) {
      return {
        ...t,
        task_window_start: newStartDate.toISOString(),
        duration: `${newDuration} days`,
      }
    }
    // All other tasks: unchanged
    return t
  })
}

/**
 * Handle dragging the end of a specific task
 * Adjusts task duration while keeping start date fixed
 * Other tasks are unaffected (no cascading)
 */
function handleTaskEndDrag(startTasks, taskIndex, deltaDays) {
  const task = startTasks[taskIndex]
  if (!task) return null

  const minDuration = MIN_TASK_DURATIONS[task.task_code] || MIN_TASK_DURATIONS.default

  const newDuration = task._startDuration + deltaDays

  // Enforce minimum duration
  if (newDuration < minDuration) {
    return null // Can't shrink below minimum
  }

  return startTasks.map((t, idx) => {
    if (idx === taskIndex) {
      return {
        ...t,
        duration: `${newDuration} days`,
      }
    }
    // All other tasks: unchanged
    return t
  })
}
